import { useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import { useLanguage } from './language-context';
import './sign-out-progress.css';

// Sign-out feedback for every sign-out entry point of this surface (the rail and account-menu sign-out pill and its confirm). One
// module-level flag, so whichever control started it, all of them disable at once, a
// second click is ignored, and one full-screen veil says "Signing out…" until the
// surface leaves. Duplicated per surface (CB-D3).
let pending = false;
const listeners = new Set<() => void>();

function setPending(next: boolean): void {
  pending = next;
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** True from the first click until the surface leaves (or the flow ends). */
export function useSigningOut(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => pending,
    () => pending,
  );
}

/**
 * Run a sign-out once. `signOut` ends the session server-side; `leave` runs after it
 * settles — resolved OR failed (the in-memory token is already dropped, so the local
 * session is over either way). Calls while one is in flight are ignored.
 */
export async function runSignOut(signOut: () => Promise<void>, leave: () => void): Promise<void> {
  if (pending) return;
  setPending(true);
  try {
    await signOut();
  } catch {
    /* best-effort server call — leave regardless */
  } finally {
    leave();
  }
}

/** Clear the flag — when the surface stays mounted after leaving, and in tests. */
export function endSignOut(): void {
  if (pending) setPending(false);
}

/** The inline spinner a sign-out control shows while pending. */
export function SignOutSpinner() {
  return <span className="signout-spinner" aria-hidden="true" />;
}

/**
 * A sign-out control's content: its own icon + label, or — while signing out — a
 * spinner + the pending text (one inline-flex span, so any button lays it out).
 */
export function SignOutLabel({ label, icon }: { label: string; icon?: ReactNode }) {
  const { t } = useLanguage();
  const signingOut = useSigningOut();
  if (!signingOut)
    return (
      <>
        {icon}
        <span>{label}</span>
      </>
    );
  return (
    <span className="signout-label">
      <SignOutSpinner />
      <span>{t('common.signing_out')}</span>
    </span>
  );
}

/** The full-screen veil shown while signing out. Mount once, near the root. */
export function SignOutOverlay() {
  const { t } = useLanguage();
  const signingOut = useSigningOut();
  if (!signingOut) return null;
  return (
    <div className="signout-overlay" role="status" aria-live="polite">
      <span className="signout-spinner signout-spinner--lg" aria-hidden="true" />
      <span className="signout-overlay__text">{t('common.signing_out')}</span>
    </div>
  );
}
